import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Search, Sparkles, Timer, ShoppingBag, Store as StoreIcon, X, Clock, Globe2 } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const CATEGORIAS = ["Todas", "Moda", "Calzado", "Tecnología", "Audio", "Wearables", "Comida", "General"];
const GEO_KEY = "plazaya_geo_v1";
const NACIONAL = "__nacional__";

function distanciaKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function Home() {
  const [plazas, setPlazas] = useState([]);
  // Zona seleccionada: "__nacional__" (Todo México, filtro por ubicación) o plaza_id
  const [zona, setZona] = useState(NACIONAL);
  const [productos, setProductos] = useState([]);
  const [tiendas, setTiendas] = useState([]);
  const [categoria, setCategoria] = useState("Todas");
  const [q, setQ] = useState("");
  const [ubicacion, setUbicacion] = useState(""); // CP, colonia, ciudad, estado
  const [estados, setEstados] = useState([]);
  const [estadoFiltro, setEstadoFiltro] = useState("");
  const [userGeo, setUserGeo] = useState(null);
  const [enRadio, setEnRadio] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const cpCercano = searchParams.get("cp") || "";
  const rangoCp = searchParams.get("rango") || "10";
  const { add } = useCart();

  useEffect(() => {
    api.get("/plazas").then(r => setPlazas(r.data.plazas));
    api.get("/tiendas/ubicaciones").then(r => setEstados(r.data.estados || [])).catch(() => {});
    try {
      const saved = JSON.parse(localStorage.getItem(GEO_KEY) || "null");
      if (saved?.lat) setUserGeo(saved);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const params = {};
    if (zona && zona !== NACIONAL) params.plaza_id = zona;
    if (categoria !== "Todas") params.categoria = categoria;
    if (q) params.q = q;
    if (ubicacion) params.location = ubicacion;
    if (estadoFiltro) params.location = estadoFiltro; // estado tiene prioridad si está definido
    if (cpCercano) { params.cp_cercano = cpCercano; params.rango_cp = rangoCp; }
    if (q && q.trim().length >= 3) {
      const geo = userGeo ? { lat: userGeo.lat, lng: userGeo.lng } : {};
      api.get("/buscar", { params: { q, cp: cpCercano || undefined, ...geo } }).then(r => {
        let res = r.data.resultados;
        if (categoria !== "Todas") res = res.filter(p => p.categoria === categoria);
        setProductos(res);
      });
    } else {
      delete params.q;
      api.get("/productos", { params: { ...params, marketplace: true } }).then(r => setProductos(r.data.productos));
    }

    const tParams = {};
    if (zona && zona !== NACIONAL) tParams.plaza_id = zona;
    if (ubicacion) tParams.location = ubicacion;
    if (estadoFiltro) tParams.estado = estadoFiltro;
    if (cpCercano) { tParams.cp_cercano = cpCercano; tParams.rango_cp = rangoCp; }
    api.get("/tiendas", { params: tParams }).then(r => setTiendas(r.data.tiendas));
  }, [zona, categoria, q, ubicacion, estadoFiltro, cpCercano, rangoCp, userGeo]);

  const currentPlaza = zona !== NACIONAL ? plazas.find(p => p.id === zona) : null;

  useEffect(() => {
    if (userGeo && currentPlaza) {
      const d = distanciaKm(userGeo.lat, userGeo.lng, currentPlaza.lat, currentPlaza.lng);
      setEnRadio(d <= currentPlaza.radio_entrega_km);
    } else {
      setEnRadio(null);
    }
  }, [userGeo, currentPlaza]);

  const modoNacional = zona === NACIONAL;

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <Navbar transparent />

      {/* Delivery badge banner */}
      <div className="bg-zinc-900 text-white text-sm">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-9 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-[#D4FF00]" />
            <span data-testid="delivery-badge" className="font-medium">Entrega garantizada en 60–90 min · Todo México</span>
          </div>
          <div className="hidden md:flex items-center gap-2 text-zinc-400">
            <span>Comercios de barrio, plazas y tiendas · Un solo envío</span>
          </div>
        </div>
      </div>

      {cpCercano && (
        <div className="bg-[#D4FF00] text-zinc-900 text-sm" data-testid="cp-filter-banner">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-10 flex items-center gap-3">
            <MapPin className="w-4 h-4" />
            <span>Mostrando comercios cerca del <b>C.P. {cpCercano}</b> (±{rangoCp})</span>
            <button data-testid="cp-filter-clear" onClick={() => setSearchParams({})} className="ml-auto inline-flex items-center gap-1 text-xs font-medium underline underline-offset-4"><X className="w-3 h-3" /> Quitar filtro</button>
          </div>
        </div>
      )}

      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-8 pb-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="lg:col-span-8 relative rounded-sm overflow-hidden bg-zinc-900 aspect-[16/10] lg:aspect-[16/9]"
            >
              <img
                src="https://images.unsplash.com/photo-1785158377535-616f63509cdd?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
                alt="en 90 MINUTOS"
                className="absolute inset-0 w-full h-full object-cover opacity-70"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/20 to-transparent" />
              <div className="relative h-full flex flex-col justify-between p-8 lg:p-12">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="overline text-white/80 bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/20">
                    <Sparkles className="w-3 h-3 inline mr-1.5 -mt-0.5" /> Hiperlocal · Todo México
                  </span>
                  {enRadio === true && (
                    <span data-testid="in-radius" className="overline text-emerald-200 bg-emerald-500/20 backdrop-blur-md px-3 py-1.5 rounded-full border border-emerald-400/40">
                      ✓ Estás en zona
                    </span>
                  )}
                  {enRadio === false && (
                    <span data-testid="out-radius" className="overline text-amber-100 bg-amber-500/20 backdrop-blur-md px-3 py-1.5 rounded-full border border-amber-400/40">
                      Fuera del radio
                    </span>
                  )}
                </div>
                <div>
                  <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
                    Compra en tu barrio.<br />
                    <span className="text-[#D4FF00]">Recibe en 90 min.</span>
                  </h1>
                  <p className="mt-4 text-white/85 max-w-lg text-base lg:text-lg font-light">
                    Le competimos a <b>Amazon, eBay y Mercado Libre</b> porque entregamos en menos de 90 minutos, no en 3 días. Miles de comercios de todo México, un solo pedido, un solo envío.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <a href="#catalogo">
                      <Button data-testid="hero-cta-explore" className="bg-[#FF3366] hover:bg-[#E62958] text-white rounded-sm h-11 px-6 font-medium">
                        Explorar productos <ArrowUpRight className="w-4 h-4 ml-1" />
                      </Button>
                    </a>
                    <Link to="/para-locatarios">
                      <Button variant="outline" className="rounded-sm h-11 px-6 border-white/40 bg-white/5 backdrop-blur text-white hover:bg-white/10 hover:text-white">
                        Soy locatario
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Panel de filtros — modo Global */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-4 flex flex-col gap-4"
            >
              <div className="rounded-sm bg-white border border-zinc-200 p-6 flex-1">
                <div className="overline text-zinc-500 mb-2">Elige tu zona</div>
                <MapPin className="w-6 h-6 text-[#FF3366] mb-3" />
                <Select value={zona} onValueChange={setZona}>
                  <SelectTrigger data-testid="plaza-selector" className="rounded-sm h-12 border-zinc-300 text-base font-medium">
                    <SelectValue placeholder="Selecciona zona" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NACIONAL} data-testid="plaza-option-nacional">
                      <span className="flex items-center gap-2"><Globe2 className="w-4 h-4" /> Todo México (nacional)</span>
                    </SelectItem>
                    {plazas.map(p => (
                      <SelectItem key={p.id} value={p.id} data-testid={`plaza-option-${p.id}`}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Filtro por estado (autocompletado desde tiendas registradas) */}
                {modoNacional && estados.length > 0 && (
                  <Select value={estadoFiltro || "__all__"} onValueChange={v => setEstadoFiltro(v === "__all__" ? "" : v)}>
                    <SelectTrigger data-testid="filter-estado" className="mt-3 rounded-sm h-10 text-sm">
                      <SelectValue placeholder="Filtrar por estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos los estados</SelectItem>
                      {estados.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}

                <Input
                  data-testid="filter-ubicacion"
                  value={ubicacion} onChange={e => setUbicacion(e.target.value)}
                  placeholder="CP, colonia, ciudad…"
                  className="mt-3 rounded-sm h-10 text-sm"
                />
                {currentPlaza && (
                  <div className="mt-3 space-y-2 text-sm text-zinc-600">
                    <div>{currentPlaza.direccion}</div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="flex items-center gap-1"><Timer className="w-3 h-3" /> {currentPlaza.horario_apertura} — {currentPlaza.horario_cierre}</span>
                      <span>Radio {currentPlaza.radio_entrega_km} km</span>
                    </div>
                    {!userGeo && (
                      <Link to="/cliente" className="mt-2 inline-block text-xs text-[#FF3366] hover:underline font-medium" data-testid="geo-recheck">
                        Buscar comercios cerca de mi C.P.
                      </Link>
                    )}
                  </div>
                )}
                {modoNacional && (
                  <div className="mt-3 text-xs text-zinc-500 flex items-start gap-1.5">
                    <Globe2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#FF3366]" />
                    <span>Mostrando comercios de <b>todo México</b>. Filtra por estado, ciudad o CP para acotar tu zona.</span>
                  </div>
                )}
              </div>

              <div className="rounded-sm bg-[#D4FF00] p-6 relative overflow-hidden">
                <ShoppingBag className="w-8 h-8 mb-3" strokeWidth={2} />
                <div className="font-display font-black text-3xl tracking-tight leading-none">Ingreso extra<br />para México</div>
                <div className="text-sm mt-2 font-medium">Miles de NENIS y repartidores generan ingresos con nosotros</div>
              </div>
            </motion.div>

            {/* Universal search */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.2 }}
              className="lg:col-span-12"
            >
              <div className="rounded-sm bg-white border border-zinc-200 p-4 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[240px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    data-testid="hero-search"
                    placeholder="Búsqueda exacta: ej. sombrero charro fieltro negro talla M"
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    className="pl-9 h-11 rounded-sm border-zinc-200"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIAS.map(c => (
                    <button
                      key={c} data-testid={`chip-${c}`} onClick={() => setCategoria(c)}
                      className={`px-4 h-10 rounded-sm text-sm font-medium transition-colors border ${
                        categoria === c ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-900"
                      }`}
                    >{c}</button>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TIENDAS carrusel */}
      {tiendas.length > 0 && (
        <section id="tiendas" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="overline text-zinc-500 mb-1">Locatarios</div>
              <h2 className="font-display font-bold text-2xl tracking-tighter">
                {modoNacional ? "Comercios en México" : "Tiendas del mall"}
                <span className="text-zinc-400 text-lg ml-3">{tiendas.length}</span>
              </h2>
            </div>
          </div>
          <TiendasCarousel tiendas={tiendas} />
        </section>
      )}

      {/* CATÁLOGO */}
      <section id="catalogo" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="overline text-zinc-500 mb-2">Catálogo</div>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter">
              {categoria === "Todas" ? "Todos los productos" : categoria}
              <span className="text-zinc-400 text-2xl ml-3">{productos.length}</span>
            </h2>
          </div>
        </div>

        {productos.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-16 text-center text-zinc-500">
            No hay productos con esos filtros. Prueba con otra zona o categoría.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {productos.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.4) }}
              >
                <ProductCard producto={p} onAdd={() => {
                  const ok = add(p);
                  if (ok !== false) toast.success(`${p.nombre} agregado`);
                }} />
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <footer id="como-funciona" className="border-t border-zinc-200 bg-white mt-12">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="font-display font-black text-2xl tracking-tight">en 90<span className="text-[#FF3366]"> MINUTOS</span></div>
            <p className="text-sm text-zinc-500 mt-3 max-w-sm">La plataforma hiperlocal de México. Comercios de barrio, plazas y tiendas en un solo lugar. Compra en varios comercios y recibe en un solo envío.</p>
          </div>
          <div>
            <div className="overline text-zinc-500 mb-3">Cómo funciona</div>
            <ol className="text-sm space-y-2 text-zinc-700">
              <li>1. Elige tu zona (o Todo México)</li>
              <li>2. Añade productos de varios comercios</li>
              <li>3. Un solo pago, un solo envío</li>
            </ol>
          </div>
          <div>
            <div className="overline text-zinc-500 mb-3">Para negocios</div>
            <Link to="/register" className="text-sm text-zinc-700 hover:text-[#FF3366]">Registra tu negocio</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function TiendasCarousel({ tiendas }) {
  const scroller = useRef(null);
  const scroll = (dir) => {
    scroller.current?.scrollBy({ left: dir * 280, behavior: "smooth" });
  };
  return (
    <div className="relative">
      <button onClick={() => scroll(-1)} className="hidden md:grid absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white border border-zinc-200 hover:border-zinc-900 place-items-center">‹</button>
      <button onClick={() => scroll(1)} className="hidden md:grid absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white border border-zinc-200 hover:border-zinc-900 place-items-center">›</button>
      <div ref={scroller} className="flex gap-3 overflow-x-auto snap-x scroll-smooth pb-2" style={{ scrollbarWidth: "thin" }}>
        {tiendas.map(t => (
          <Link
            key={t.id}
            to={`/t/${t.id}`}
            data-testid={`tienda-card-${t.id}`}
            className="snap-start shrink-0 w-56 bg-white border border-zinc-200 rounded-sm overflow-hidden card-lift"
          >
            <div className="aspect-[16/10] bg-zinc-100 overflow-hidden">
              {t.imagen_url ? (
                <img src={t.imagen_url} alt={t.nombre_local} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center"><StoreIcon className="w-8 h-8 text-zinc-300" /></div>
              )}
            </div>
            <div className="p-3">
              <div className="font-display font-semibold tracking-tight truncate">{t.nombre_local}</div>
              <div className="text-xs text-zinc-500 mt-0.5 truncate">
                {t.ciudad ? `${t.ciudad}${t.estado ? `, ${t.estado}` : ""}` : (t.numero_local && t.numero_local !== "N/A" ? `Local ${t.numero_local}` : "Independiente")} · {t.categoria}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function ProductCard({ producto, onAdd }) {
  const agotado = !producto.estado_disponible || producto.stock_disponible === 0;
  return (
    <div className="card-lift bg-white border border-zinc-200 rounded-sm overflow-hidden flex flex-col">
      <Link to={`/producto/${producto.id}`} className="relative aspect-square bg-zinc-100 overflow-hidden block">
        {producto.imagen_url ? (
          <img src={producto.imagen_url} alt={producto.nombre} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center text-zinc-400 text-sm">Sin imagen</div>
        )}
        {agotado && (
          <div className="absolute inset-0 bg-black/60 grid place-items-center">
            <span className="text-white font-display font-bold tracking-tight text-lg">AGOTADO</span>
          </div>
        )}
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <div className="overline text-zinc-400">{producto.categoria}{producto.eta_min ? ` · llega en ~${producto.eta_min} min` : ""}</div>
        {producto.atributos_detectados && Object.keys(producto.atributos_detectados).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">{Object.entries(producto.atributos_detectados).map(([k, v]) => <span key={k} className="text-[9px] bg-zinc-100 px-1.5 py-0.5 rounded-sm">{v}</span>)}</div>
        )}
        <Link to={`/producto/${producto.id}`} className="font-display font-semibold text-base mt-1 tracking-tight hover:text-[#FF3366] line-clamp-1">
          {producto.nombre}
        </Link>
        <div className="flex items-end justify-between mt-3">
          <div className="font-display font-black text-xl tracking-tight">${producto.precio.toFixed(2)}</div>
          <Button
            data-testid={`add-to-cart-${producto.id}`}
            onClick={onAdd}
            disabled={agotado}
            size="sm"
            className="rounded-sm bg-zinc-900 hover:bg-[#FF3366] text-white h-8 px-3 disabled:opacity-40"
          >+</Button>
        </div>
        <Link
          to={`/t/${producto.tienda_id}`}
          data-testid={`store-link-${producto.id}`}
          className="mt-3 pt-3 border-t border-zinc-100 text-[11px] uppercase tracking-wider font-medium text-[#FF3366] hover:text-zinc-900 flex items-center gap-1"
        >
          Conoce la tienda online del anunciante <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}
